import BasePriceTable from "@/components/base-price/BasePriceTable";
import Breadcrumb from "@/components/Breadcrumb";
import {
  useAddQuarterReturnScrapPrice,
  useCreateReturnScrapPrice,
  useGetReturnScrap,
  useGetReturnScrapPrice,
  useUpdateReturnScrapPrice,
} from "@/hooks/masterHook";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
  NormalSecondaryButton,
} from "@/styles/globalstyles";
import { SuccessAlert, ErrorAlert, ConfirmAlert, WarningAlert } from "@/utils/alert";
import { getCurrentQuarter } from "@/utils/date";
import { getAxiosErrorMessage } from "@/utils/healper";
import { Add } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const ReturnScrapManagement = () => {
  const canEdit = usePermissions().canEdit("return.scrap");
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(dayjs().year());
  const [quarter, setQuarter] = useState<number>(0);
  const [shouldAddQuarter, setShouldAddQuarter] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFlagged, setIsFlagged] = useState<boolean>(false);
  const [editData, setEditData] = useState<Record<string, string | number>[]>(
    [],
  );

  const {
    data: basePrices,
    isLoading: isBasePriceLoading,
    isError: isBasePriceError,
  } = useGetReturnScrapPrice(year);

  const {
    data: returnScrapData,
    isLoading: isReturnScrapLoading,
    isError: isReturnScrapError,
  } = useGetReturnScrap({ active_only: true }, true);

  const { mutate: createScrapPrice, isPending: isPendingCreateScrapPrice } =
    useCreateReturnScrapPrice();

  const { mutate: updateScrapPrice, isPending: isPendingUpdateScrapPrice } =
    useUpdateReturnScrapPrice();

  const { mutate: createNewQuarterBasePrices, isPending: isPendingAddQuarter } =
    useAddQuarterReturnScrapPrice();

  useQueryHandler({
    isFetching:
      isBasePriceLoading ||
      isReturnScrapLoading ||
      isPendingUpdateScrapPrice ||
      isPendingCreateScrapPrice ||
      isPendingAddQuarter,
    isError: isBasePriceError || isReturnScrapError,
  });

  const allQuarters: number[] = useMemo(() => {
    const allQ = basePrices?.scrap_prices?.map((price) => price.quarter) || [];
    setQuarter(Math.max(...allQ) || 0);
    if (
      year === dayjs().year() &&
      (!Math.max(...allQ) || Math.max(...allQ) < getCurrentQuarter())
    ) {
      setShouldAddQuarter(true);
    } else {
      setShouldAddQuarter(false);
    }
    return allQ;
  }, [basePrices, year]);

  const tableData: Array<Record<string, string | number>> = useMemo(() => {
    if (!quarter) return [];
    return (
      returnScrapData?.map((supplier) => {
        const rowData: Record<string, string | number> = {
          supplier_name: supplier.name,
          supplier_id: supplier.id,
        };

        const quarterPrices = basePrices?.scrap_prices?.find((data) => {
          return data.quarter === quarter;
        });
        const priceData = quarterPrices?.data?.find(
          (price) => price.ms_return_scrap_id === supplier.id,
        );
        rowData.price = priceData?.price || "";
        return rowData;
      }) || []
    );
  }, [basePrices, returnScrapData, quarter]);

  const handleChangeQuarterTab = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setQuarter(newValue);
  };

  const handleAddQuarter = () => {
    ConfirmAlert(
      "ยืนยันการเพิ่ม Quarter",
      `คุณต้องการเพิ่ม Return Scrap Price สำหรับ Q${getCurrentQuarter()} ปี ${year} หรือไม่?`,
      "ยืนยัน",
      "ยกเลิก",
    ).then((result) => {
      if (result) {
        proceedAddQuarter();
      }
    });
  };

  const proceedAddQuarter = () => {
    createNewQuarterBasePrices(
      {
        target_quarter: getCurrentQuarter(),
        target_year: dayjs().year(),
      },
      {
        onSuccess: () => {
          SuccessAlert("เพิ่ม Quarter สำเร็จ", "");
          setQuarter(getCurrentQuarter());
        },
        onError: (error) => {
          ErrorAlert("เพิ่ม Quarter ไม่สำเร็จ", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleFlagCheckboxChange = (checked: boolean) => {
    if (checked) {
      WarningAlert(
        "Warning",
        "การบันทึกด้วยตัวเลือกนี้จะทำให้ Part ที่เกี่ยวข้องถูกติด Flagged สำหรับ Buyer ซึ่งต้องให้หัวหน้า Buyer เป็นผู้ปลด Flag เท่านั้น",
      );
    }
    setIsFlagged(checked);
  };

  const handleSave = () => {
    const changedPrices: Array<{
      scrap_price_id: number;
      price: number;
    }> = [];
    const newPrice: Array<{ ms_return_scrap_id: number; price: number }> = [];

    editData?.forEach((editRow, index) => {
      const originalRow = tableData[index];
      const supplier_id = editRow["supplier_id"] as number;
      const editValue = editRow["price"];
      const originalValue = originalRow["price"];

      // หา priceData ของ supplier นี้ใน quarter ปัจจุบัน
      const quarterPrices = basePrices?.scrap_prices?.find(
        (price) => price.quarter === quarter,
      );
      const existingPriceData = quarterPrices?.data?.find(
        (priceData) => priceData.ms_return_scrap_id === supplier_id,
      );

      // ถ้ามีข้อมูลอยู่แล้วและมีการเปลี่ยนแปลง -> update
      if (existingPriceData) {
        if (editValue !== originalValue && editValue !== "") {
          changedPrices.push({
            scrap_price_id: existingPriceData.scrap_price_id,
            price: parseFloat(editValue as string) || 0,
          });
        }
      }
      // ถ้ายังไม่มีข้อมูลและมีการกรอกราคา -> create new
      else if (editValue !== "" && editValue !== originalValue) {
        newPrice.push({
          ms_return_scrap_id: supplier_id,
          price: parseFloat(editValue as string) || 0,
        });
      }
    });

    if (changedPrices.length > 0) {
      updateScrapPrice(
        {
          updates: changedPrices,
          is_buyer_flagged: isFlagged,
        },
        {
          onSuccess: () => {
            SuccessAlert("แก้ไข Return Scrap Price สำเร็จ", "");
          },
          onError: (error) => {
            ErrorAlert(
              "แก้ไข Return Scrap Price ไม่สำเร็จ",
              getAxiosErrorMessage(error),
            );
          },
        },
      );
    }

    if (newPrice.length > 0) {
      createScrapPrice(
        {
          quarter: quarter,
          year: year,
          prices: newPrice,
          is_buyer_flagged: isFlagged,
        },
        {
          onSuccess: () => {
            SuccessAlert("เพิ่ม Return Scrap Price สำเร็จ", "");
          },
          onError: (error) => {
            ErrorAlert(
              "เพิ่ม Return Scrap Price ไม่สำเร็จ",
              getAxiosErrorMessage(error),
            );
          },
        },
      );
    }

    setIsEditing(false);
    setIsFlagged(false);
  };

  useEffect(() => {
    document.title = "Return Scrap Supplier | AMPA";
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData([...tableData]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData([]);
    setIsFlagged(false);
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb current="return.scrap" />
      <Box mt={1}>
        <Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          size="small"
        >
          <MenuItem key={dayjs().year() - 1} value={dayjs().year() - 1}>
            {dayjs().year() - 1}
          </MenuItem>
          <MenuItem key={dayjs().year()} value={dayjs().year()}>
            {dayjs().year()}
          </MenuItem>
        </Select>
        <Box
          mt={1}
          display="flex"
          justifyContent="space-between"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Tabs
              value={quarter}
              onChange={handleChangeQuarterTab}
              aria-label="tabs"
            >
              {allQuarters?.length > 0 &&
                allQuarters.map((q) => (
                  <Tab key={q} label={`Q${q}`} value={q} />
                ))}
            </Tabs>
            {shouldAddQuarter && (
              <Button
                variant="contained"
                size="small"
                onClick={handleAddQuarter}
                disabled={!canEdit}
                sx={{ ...DefaultPrimaryButton, my: 1, fontSize: "0.75rem" }}
              >
                <Add /> เพิ่ม Quarter
              </Button>
            )}
          </Box>
          {!isEditing && (
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  ...DefaultSecondaryButton,
                  alignItems: "center",
                  fontSize: "0.875rem",
                }}
                onClick={() => {
                  navigate("/return-scrap/master");
                }}
                disabled={!canEdit}
              >
                จัดการ Return Scrap Supplier
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleEdit}
                disabled={
                  !canEdit ||
                  quarter != getCurrentQuarter() ||
                  year != dayjs().year()
                }
                sx={{ ...DefaultSecondaryButton, fontSize: "0.875rem" }}
              >
                <EditIcon /> แก้ไข Base Price
              </Button>
            </Box>
          )}
          {isEditing && (
            <Box sx={{ my: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFlagged}
                    onChange={(e) => handleFlagCheckboxChange(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label="ต้องการให้ติด Flagged สำหรับ Buyer"
                sx={{ mr: 1 }}
              />
              <Button
                variant="outlined"
                sx={{ ...NormalSecondaryButton, fontSize: "0.875rem" }}
                onClick={handleCancel}
              >
                ยกเลิก
              </Button>
              <Button
                variant="outlined"
                sx={{ ...DefaultPrimaryButton, fontSize: "0.875rem" }}
                onClick={handleSave}
              >
                บันทึก
              </Button>
            </Box>
          )}
        </Box>
        <BasePriceTable
          columns={["price"]}
          data={tableData}
          isEditing={isEditing}
          editData={editData}
          setEditData={setEditData}
        />
      </Box>
    </Box>
  );
};

export default ReturnScrapManagement;
